import { Alert, Descriptions, Spin, Typography } from 'antd';
import { formatNumber, MoneyText } from '../../lib/format';
import type { LiveTotals } from './totals';

const { Text } = Typography;

/**
 * The editor's live totals panel: subtotal, one row per VAT group from the
 * shared `computeInvoiceTotals` breakdown, and the grand total. Every value
 * shown here is the value the server will persist for this exact line set —
 * see totals.ts for why that is a guarantee rather than a coincidence.
 */
export function TotalsPanel({
  totals,
  showVat,
  currency = 'RON',
}: {
  totals: LiveTotals;
  showVat: boolean;
  currency?: string;
}) {
  if (totals.status === 'loading') {
    // `/vat-rates` hasn't answered yet — a neutral spinner, never the "fără
    // cotă valabilă" alarm below (that alarm is only meaningful once the
    // rates have actually arrived and a category is genuinely missing one).
    return (
      <div style={{ textAlign: 'center', padding: '16px 0' }}>
        <Spin size="small" />
      </div>
    );
  }

  if (totals.status === 'unresolved-vat') {
    return (
      <Alert
        type="warning"
        showIcon
        message="Totaluri indisponibile"
        description="Una dintre liniile facturii folosește o categorie de TVA fără cotă valabilă astăzi. Alege altă categorie pentru a putea salva factura."
      />
    );
  }

  const subtotal = totals.status === 'ok' ? totals.totals.subtotal : '0.00';
  const total = totals.status === 'ok' ? totals.totals.total : '0.00';
  const breakdown = totals.status === 'ok' ? totals.totals.breakdown : [];

  return (
    <Descriptions bordered column={1} size="small">
      <Descriptions.Item label="Subtotal">
        <MoneyText value={subtotal} currency={currency} />
      </Descriptions.Item>
      {showVat
        ? breakdown.map((group) => (
            <Descriptions.Item
              key={`${group.vatCategoryCode}-${group.vatRatePercent}`}
              label={`TVA ${formatNumber(group.vatRatePercent)}%`}
            >
              <MoneyText value={group.vatAmount} currency={currency} />
            </Descriptions.Item>
          ))
        : null}
      <Descriptions.Item label={<Text strong>Total</Text>}>
        <Text strong>
          <MoneyText value={total} currency={currency} />
        </Text>
      </Descriptions.Item>
    </Descriptions>
  );
}
